import { useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format, startOfMonth, endOfMonth, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import type { DateRange } from 'react-day-picker'
import {
    CalendarIcon,
    Download,
    Loader2,
    Package,
    Coins,
    Users,
    TrendingUp,
    UserPlus,
    BarChart3,
    Euro,
    ClipboardList,
    ChevronLeft,
    ChevronRight,
    Gift,
    HeartPulse
} from 'lucide-react'

import { MemberActivityReport } from '@/components/admin/reports/member-activity-report'
import { OccupancyHeatmap, type OccupancyMetric } from '@/components/admin/reports/occupancy-heatmap'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import {
    getAdminPurchasedPackagesReport,
    getAdminTokenUsageReport,
    getAdminAttendanceReport,
    getAdminFinancialReport,
    getAdminTrainerActivityReport,
    getAdminUserGrowthReport,
    getAdminVoucherRedemptionsReport
} from '@/lib/api'
import { DEFAULT_TABLE_PAGE_SIZE } from '@/lib/constants'
import { queryKeys } from '@/lib/query-keys'
import { formatCreditAmount } from '@/lib/subscriptions'
import { cn } from '@/lib/utils'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"

const ATTENDANCE_VIEWS: { value: OccupancyMetric | 'sessions'; label: string }[] = [
    { value: 'occupancy', label: 'Ø Auslastung' },
    { value: 'full', label: 'Anteil ausgebucht' },
    { value: 'sessions', label: 'Einzeltermine' },
]

export function AdminReportsPage() {
    const [attendanceView, setAttendanceView] = useState<OccupancyMetric | 'sessions'>('occupancy')
    const [dateRange, setDateRange] = useState<{
        from: Date
        to: Date
    }>({
        from: startOfMonth(new Date()),
        to: endOfMonth(new Date()),
    })

    const PAGE_SIZE = DEFAULT_TABLE_PAGE_SIZE
    const [packagesPage, setPackagesPage] = useState(1)
    const [tokenUsagePage, setTokenUsagePage] = useState(1)
    const [attendancePage, setAttendancePage] = useState(1)
    const [financialPage, setFinancialPage] = useState(1)
    const [trainerActivityPage, setTrainerActivityPage] = useState(1)
    const [userGrowthPage, setUserGrowthPage] = useState(1)
    const [voucherRedemptionsPage, setVoucherRedemptionsPage] = useState(1)

    const commonParams = {
        from: dateRange.from.toISOString(),
        to: dateRange.to.toISOString(),
    }

    const packagesQuery = useQuery({
        queryKey: [queryKeys.admin, 'reports', 'packages', commonParams, packagesPage],
        queryFn: () => getAdminPurchasedPackagesReport({ ...commonParams, page: packagesPage, pageSize: PAGE_SIZE }),
    })

    const tokenUsageQuery = useQuery({
        queryKey: [queryKeys.admin, 'reports', 'token-usage', commonParams, tokenUsagePage],
        queryFn: () => getAdminTokenUsageReport({ ...commonParams, page: tokenUsagePage, pageSize: PAGE_SIZE }),
    })

    const attendanceQuery = useQuery({
        queryKey: [queryKeys.admin, 'reports', 'attendance', commonParams, attendancePage],
        queryFn: () => getAdminAttendanceReport({ ...commonParams, page: attendancePage, pageSize: PAGE_SIZE }),
    })

    const financialQuery = useQuery({
        queryKey: [queryKeys.admin, 'reports', 'financial', commonParams, financialPage],
        queryFn: () => getAdminFinancialReport({ ...commonParams, page: financialPage, pageSize: PAGE_SIZE }),
    })

    const trainerActivityQuery = useQuery({
        queryKey: [queryKeys.admin, 'reports', 'trainer-activity', commonParams, trainerActivityPage],
        queryFn: () => getAdminTrainerActivityReport({ ...commonParams, page: trainerActivityPage, pageSize: PAGE_SIZE }),
    })

    const userGrowthQuery = useQuery({
        queryKey: [queryKeys.admin, 'reports', 'user-growth', commonParams, userGrowthPage],
        queryFn: () => getAdminUserGrowthReport({ ...commonParams, page: userGrowthPage, pageSize: PAGE_SIZE }),
    })

    const voucherRedemptionsQuery = useQuery({
        queryKey: queryKeys.voucherReports({ ...commonParams, page: voucherRedemptionsPage, pageSize: PAGE_SIZE }),
        queryFn: () => getAdminVoucherRedemptionsReport({ ...commonParams, page: voucherRedemptionsPage, pageSize: PAGE_SIZE }),
    })

    const formatCurrency = (cents: number) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(cents / 100)
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold">Berichte & Analysen</h1>
                    <p className="text-muted-foreground">Umfassende Auswertungen über dein Studio.</p>
                </div>

                <div className="flex items-center gap-2">
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={cn(
                                    'w-[280px] justify-start text-left font-normal',
                                    !dateRange && 'text-muted-foreground',
                                )}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {dateRange?.from ? (
                                    dateRange.to ? (
                                        <>
                                            {format(dateRange.from, 'dd. MMM yyyy', { locale: de })} -{' '}
                                            {format(dateRange.to, 'dd. MMM yyyy', { locale: de })}
                                        </>
                                    ) : (
                                        format(dateRange.from, 'dd. MMM yyyy', { locale: de })
                                    )
                                ) : (
                                    <span>Zeitraum wählen</span>
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[calc(100vw-2rem)] max-w-[42rem] p-0" align="end">
                            <Calendar
                                initialFocus
                                mode="range"
                                showOutsideDays={false}
                                defaultMonth={dateRange?.from}
                                selected={{
                                    from: dateRange?.from,
                                    to: dateRange?.to,
                                }}
                                numberOfMonths={2}
                                locale={de}
                                onSelect={(range: DateRange | undefined) => {
                                    if (range?.from && range?.to) {
                                        setDateRange({ from: range.from, to: range.to })
                                        setPackagesPage(1)
                                        setTokenUsagePage(1)
                                        setAttendancePage(1)
                                        setFinancialPage(1)
                                        setTrainerActivityPage(1)
                                        setUserGrowthPage(1)
                                        setVoucherRedemptionsPage(1)
                                    }
                                }}
                            />
                        </PopoverContent>
                    </Popover>
                    <Button variant="outline" size="icon" disabled title="Export (Demnächst)">
                        <Download className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <Tabs defaultValue="packages" className="space-y-6">
                <TabsList className="bg-muted/50 w-full justify-start overflow-x-auto h-auto p-1">
                    <TabsTrigger value="packages" className="gap-2 px-4 py-2">
                        <Package className="h-4 w-4" />
                        Pakete
                    </TabsTrigger>
                    <TabsTrigger value="token-usage" className="gap-2 px-4 py-2">
                        <Coins className="h-4 w-4" />
                        Token
                    </TabsTrigger>
                    <TabsTrigger value="attendance" className="gap-2 px-4 py-2">
                        <Users className="h-4 w-4" />
                        Auslastung
                    </TabsTrigger>
                    <TabsTrigger value="financial" className="gap-2 px-4 py-2">
                        <Euro className="h-4 w-4" />
                        Finanzen
                    </TabsTrigger>
                    <TabsTrigger value="trainer" className="gap-2 px-4 py-2">
                        <BarChart3 className="h-4 w-4" />
                        Trainer
                    </TabsTrigger>
                    <TabsTrigger value="growth" className="gap-2 px-4 py-2">
                        <TrendingUp className="h-4 w-4" />
                        Wachstum
                    </TabsTrigger>
                    <TabsTrigger value="retention" className="gap-2 px-4 py-2">
                        <HeartPulse className="h-4 w-4" />
                        Bindung
                    </TabsTrigger>
                    <TabsTrigger value="vouchers" className="gap-2 px-4 py-2">
                        <Gift className="h-4 w-4" />
                        Gutscheine
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="packages">
                    <Card>
                        <CardHeader>
                            <CardTitle>Gekaufte Pakete</CardTitle>
                            <CardDescription>Einzelne Paketverkäufe in diesem Zeitraum.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {packagesQuery.isLoading ? (
                                <div className="flex h-40 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                            ) : packagesQuery.data?.data.length ? (
                                <div className="space-y-4">
                                    <ReportTable
                                        headers={['Datum', 'Teilnehmer', 'Paket', 'Preis', 'Credits']}
                                        rows={packagesQuery.data.data.map(item => [
                                            format(parseISO(item.created_at), 'dd.MM.yy HH:mm'),
                                            item.full_name || item.email,
                                            item.package_name,
                                            formatCurrency(item.amount_paid_cents),
                                            item.credit_summary ?? (item.credits_included === 0 ? '-' : formatCreditAmount(item.credits_included))
                                        ])}
                                    />
                                    <Pagination
                                        currentPage={packagesPage}
                                        total={packagesQuery.data.total}
                                        pageSize={PAGE_SIZE}
                                        onPageChange={setPackagesPage}
                                    />
                                </div>
                            ) : <EmptyState />}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="token-usage">
                    <Card>
                        <CardHeader>
                            <CardTitle>Token Verbrauch</CardTitle>
                            <CardDescription>Verbrauchte Credits pro Kurs & Trainer.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {tokenUsageQuery.isLoading ? (
                                <div className="flex h-40 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                            ) : tokenUsageQuery.data?.data.length ? (
                                <div className="space-y-4">
                                    <ReportTable
                                        headers={['Kurs', 'Trainer', 'Verbrauch']}
                                        rows={tokenUsageQuery.data.data.sort((a, b) => b.token_count - a.token_count).map(item => [
                                            item.course_name,
                                            item.trainer_name || '-',
                                            `${formatCreditAmount(item.token_count)} ${item.credit_name ?? ""}`
                                        ])}
                                        alignRight={[2]}
                                    />
                                    <Pagination
                                        currentPage={tokenUsagePage}
                                        total={tokenUsageQuery.data.total}
                                        pageSize={PAGE_SIZE}
                                        onPageChange={setTokenUsagePage}
                                    />
                                </div>
                            ) : <EmptyState />}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="attendance">
                    <Card>
                        <CardHeader className="gap-4 md:flex-row md:items-start md:justify-between">
                            <div className="space-y-1.5">
                                <CardTitle>Kursauslastung</CardTitle>
                                <CardDescription>
                                    {attendanceView === 'sessions'
                                        ? 'Wie voll waren die einzelnen Termine?'
                                        : 'Wie ausgebucht sind die einzelnen Slots nach Wochentag und Uhrzeit?'}
                                </CardDescription>
                            </div>
                            <div className="inline-flex w-fit flex-wrap rounded-md border p-0.5">
                                {ATTENDANCE_VIEWS.map(view => (
                                    <Button
                                        key={view.value}
                                        size="sm"
                                        variant={attendanceView === view.value ? 'secondary' : 'ghost'}
                                        onClick={() => setAttendanceView(view.value)}
                                    >
                                        {view.label}
                                    </Button>
                                ))}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {attendanceView !== 'sessions' ? (
                                <OccupancyHeatmap {...commonParams} metric={attendanceView} />
                            ) : attendanceQuery.isLoading ? (
                                <div className="flex h-40 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                            ) : attendanceQuery.data?.data.length ? (
                                <div className="space-y-4">
                                    <ReportTable
                                        headers={['Datum', 'Kurs', 'Auslastung', '%']}
                                        rows={attendanceQuery.data.data.map(item => [
                                            format(parseISO(item.starts_at), 'dd.MM.yy HH:mm'),
                                            item.course_name,
                                            `${item.confirmed_count} / ${item.max_participants}`,
                                            <div key={item.session_id} className="w-full bg-muted rounded-full h-2 max-w-[100px]">
                                                <div
                                                    className={cn("h-full rounded-full", item.utilization_percent > 80 ? "bg-green-500" : item.utilization_percent > 40 ? "bg-yellow-500" : "bg-red-500")}
                                                    style={{ width: `${Math.min(item.utilization_percent, 100)}%` }}
                                                ></div>
                                            </div>
                                        ])}
                                    />
                                    <Pagination
                                        currentPage={attendancePage}
                                        total={attendanceQuery.data.total}
                                        pageSize={PAGE_SIZE}
                                        onPageChange={setAttendancePage}
                                    />
                                </div>
                            ) : <EmptyState />}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="financial">
                    <Card>
                        <CardHeader>
                            <CardTitle>Finanzielle Übersicht</CardTitle>
                            <CardDescription>Einnahmen nach Pakettyp.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {financialQuery.isLoading ? (
                                <div className="flex h-40 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                            ) : financialQuery.data?.data.length ? (
                                <div className="space-y-4">
                                    <ReportTable
                                        headers={['Pakettyp', 'Anzahl', 'Gesamtumsatz']}
                                        rows={financialQuery.data.data.map(item => [
                                            item.package_name,
                                            item.count,
                                            formatCurrency(item.total_revenue_cents)
                                        ])}
                                        alignRight={[1, 2]}
                                    />
                                    <div className="mt-4 p-4 bg-muted/30 rounded-lg flex justify-between items-center">
                                        <span className="font-bold">Gesamtumsatz (Seite):</span>
                                        <span className="text-xl font-bold text-primary">
                                            {formatCurrency(financialQuery.data.data.reduce((acc, curr) => acc + curr.total_revenue_cents, 0))}
                                        </span>
                                    </div>
                                    <Pagination
                                        currentPage={financialPage}
                                        total={financialQuery.data.total}
                                        pageSize={PAGE_SIZE}
                                        onPageChange={setFinancialPage}
                                    />
                                </div>
                            ) : <EmptyState />}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="trainer">
                    <Card>
                        <CardHeader>
                            <CardTitle>Trainer Aktivitäten</CardTitle>
                            <CardDescription>Geleistete Stunden und geschätzte Honorare.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {trainerActivityQuery.isLoading ? (
                                <div className="flex h-40 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                            ) : trainerActivityQuery.data?.data.length ? (
                                <div className="space-y-4">
                                    <ReportTable
                                        headers={['Trainer', 'Termine', 'Teilnehmer', 'Gesch. Honorar']}
                                        rows={trainerActivityQuery.data.data.map(item => [
                                            item.full_name,
                                            <Dialog key={item.trainer_id}>
                                                <DialogTrigger asChild>
                                                    <Button variant="link" className="h-auto p-0 underline decoration-dotted underline-offset-4">
                                                        {item.session_count}
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent className="max-w-md">
                                                    <DialogHeader>
                                                        <DialogTitle>Termine von {item.full_name}</DialogTitle>
                                                        <DialogDescription>Alle durchgeführten Kurse im gewählten Zeitraum.</DialogDescription>
                                                    </DialogHeader>
                                                    <div className="space-y-2 mt-4 max-h-[60vh] overflow-y-auto pr-2">
                                                        {item.sessions.sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()).map(s => (
                                                            <div key={s.id} className="flex items-center justify-between p-2 rounded-md bg-muted/30 border border-border/50 text-sm">
                                                                <div className="flex flex-col">
                                                                    <span className="font-medium text-primary">{s.course_name}</span>
                                                                    <span className="text-[11px] text-muted-foreground">{format(parseISO(s.starts_at), 'dd.MM.yyyy HH:mm')}</span>
                                                                </div>
                                                                <div className="text-right">
                                                                    <span className="text-xs text-muted-foreground mr-1">TN:</span>
                                                                    <span className="font-bold">{s.participant_count}</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </DialogContent>
                                            </Dialog>,
                                            item.total_participants,
                                            formatCurrency(item.projected_honorarium_cents)
                                        ])}
                                        alignRight={[1, 2, 3]}
                                    />
                                    <Pagination
                                        currentPage={trainerActivityPage}
                                        total={trainerActivityQuery.data.total}
                                        pageSize={PAGE_SIZE}
                                        onPageChange={setTrainerActivityPage}
                                    />
                                </div>
                            ) : <EmptyState />}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="growth">
                    <Card>
                        <CardHeader>
                            <CardTitle>Teilnehmer Wachstum</CardTitle>
                            <CardDescription>Neue Registrierungen im Zeitverlauf.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {userGrowthQuery.isLoading ? (
                                <div className="flex h-40 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                            ) : userGrowthQuery.data?.data.length ? (
                                <div className="space-y-4">
                                    <ReportTable
                                        headers={['Datum', 'Neue Teilnehmer', 'Gesamt (bis dato)']}
                                        rows={userGrowthQuery.data.data.map(item => [
                                            format(parseISO(item.date), 'dd.MM.yyyy'),
                                            <Dialog key={item.date}>
                                                <DialogTrigger asChild>
                                                    <Button variant="ghost" className="h-auto p-1 px-2 gap-2 text-primary hover:text-primary hover:bg-primary/10">
                                                        <UserPlus className="h-3 w-3 text-green-500" />
                                                        {item.new_users}
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent className="max-w-md">
                                                    <DialogHeader>
                                                        <DialogTitle>Registrierungen am {format(parseISO(item.date), 'dd.MM.yyyy')}</DialogTitle>
                                                        <DialogDescription>{item.new_users} neue Teilnehmer sind dazugekommen.</DialogDescription>
                                                    </DialogHeader>
                                                    <div className="space-y-2 mt-4 max-h-[60vh] overflow-y-auto pr-2">
                                                        {item.registered_users.map(u => (
                                                            <div key={u.id} className="p-3 rounded-md bg-muted/30 border border-border/50 flex flex-col gap-1">
                                                                <span className="font-bold text-sm">{u.full_name || 'Unbekannt'}</span>
                                                                <span className="text-xs text-muted-foreground">{u.email}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </DialogContent>
                                            </Dialog>,
                                            item.total_users_so_far
                                        ])}
                                    />
                                    <Pagination
                                        currentPage={userGrowthPage}
                                        total={userGrowthQuery.data.total}
                                        pageSize={PAGE_SIZE}
                                        onPageChange={setUserGrowthPage}
                                    />
                                </div>
                            ) : <EmptyState />}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="retention">
                    <MemberActivityReport {...commonParams} />
                </TabsContent>

                <TabsContent value="vouchers">
                    <Card>
                        <CardHeader>
                            <CardTitle>Gutschein-Einlösungen</CardTitle>
                            <CardDescription>Zuletzt eingelöste Gutschein-Codes und gutgeschriebene Credits.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {voucherRedemptionsQuery.isLoading ? (
                                <div className="flex h-40 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                            ) : voucherRedemptionsQuery.data?.data.length ? (
                                <div className="space-y-4">
                                    <ReportTable
                                        headers={['Datum', 'Teilnehmer', 'Code', 'Credits']}
                                        rows={voucherRedemptionsQuery.data.data.map(item => [
                                            format(parseISO(item.redeemed_at), 'dd.MM.yy HH:mm'),
                                            item.full_name || item.email,
                                            <span key={item.redemption_id} className="font-mono">{item.voucher_code}</span>,
                                            `${formatCreditAmount(item.credits_granted)} ${item.credit_name ?? ""}`
                                        ])}
                                        alignRight={[3]}
                                    />
                                    <Pagination
                                        currentPage={voucherRedemptionsPage}
                                        total={voucherRedemptionsQuery.data.total}
                                        pageSize={PAGE_SIZE}
                                        onPageChange={setVoucherRedemptionsPage}
                                    />
                                </div>
                            ) : <EmptyState />}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    )
}

function ReportTable({
    headers,
    rows,
    alignRight = []
}: {
    headers: string[]
    rows: ReactNode[][]
    alignRight?: number[]
}) {
    return (
        <div className="rounded-md border border-border/50">
            <Table>
                <TableHeader>
                    <TableRow className="bg-muted/30">
                        {headers.map((h, i) => (
                            <TableHead key={i} className={cn(alignRight.includes(i) && "text-right")}>{h}</TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row, i) => (
                        <TableRow key={i} className="hover:bg-muted/20">
                            {row.map((cell, j) => (
                                <TableCell key={j} className={cn(alignRight.includes(j) && "text-right tabular-nums")}>{cell}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    )
}

function EmptyState() {
    return (
        <div className="flex h-40 flex-col items-center justify-center text-muted-foreground gap-2">
            <ClipboardList className="h-8 w-8 opacity-20" />
            <p>Keine Daten für diesen Zeitraum gefunden.</p>
        </div>
    )
}

function Pagination({ currentPage, total, pageSize, onPageChange }: { currentPage: number, total: number, pageSize: number, onPageChange: (page: number) => void }) {
    const totalPages = Math.ceil(total / pageSize)
    if (totalPages <= 1) return null

    return (
        <div className="flex items-center justify-between px-2 py-4 border-t border-border/50">
            <div className="text-sm text-muted-foreground">
                Seite <span className="font-medium">{currentPage}</span> von <span className="font-medium">{totalPages}</span>
                <span className="ml-2">({total} Ergebnisse)</span>
            </div>
            <div className="flex items-center gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Zurück
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                >
                    Weiter
                    <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
            </div>
        </div>
    )
}
